import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Nav, Spinner, Alert, Modal, Button } from 'react-bootstrap';
import { FaEye, FaEdit, FaTrash, FaUserPlus, FaSync, FaEnvelope, FaPhone, FaCalendarAlt, FaCircle } from 'react-icons/fa';
import './UsersManagement.scss';
import { useAuth } from '../../context/AuthContext';

const UsersManagement = () => {
  const [key, setKey] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { token } = useAuth();

  // Admin users data (static for now)
  const adminUsers = [
    { 
      id: 1, 
      name: 'Admin User', 
      email: 'admin@example.com', 
      phone: '+1234567893',
      role: 'Super Admin',
      lastLogin: '2025-04-05'
    },
    { 
      id: 2, 
      name: 'Property Manager', 
      email: 'manager@example.com', 
      phone: '+1234567894',
      role: 'Property Manager',
      lastLogin: '2025-04-04'
    }
  ];

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Fetch users from the API
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('https://realestate.bonlineco.com/api/admin/users', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      if (data.success) {
        setUsers(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch users');
      }

      // Fetch complete
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load users when component mounts
  useEffect(() => {
    if (token) {
      fetchUsers();
    }
  }, [token]);

  // Handle view user details
  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  // Handle close view modal
  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedUser(null);
  };

  // Handle delete user
  const handleDeleteUser = useCallback(async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    
    setDeleteLoading(true);
    try {
      const response = await fetch(`https://realestate.bonlineco.com/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      if (data.success) {
        // Remove the deleted user from the state
        setUsers(users.filter(user => user.id !== userId));
        setSuccessMessage('User deleted successfully');
      } else {
        throw new Error(data.message || 'Failed to delete user');
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.message);
    } finally {
      setDeleteLoading(false);
    }
  }, [token, users]);

  return (
    <div className="users-management">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="page-title">Users Management</h2>
        <button className="btn btn-primary">
          <FaUserPlus className="me-2" />
          Add New User
        </button>
      </div>

      {successMessage && (
        <Alert variant="success" className="mb-4">
          {successMessage}
        </Alert>
      )}

      <Nav variant="tabs" activeKey={key} onSelect={(k) => setKey(k)}>
        <Nav.Item>
          <Nav.Link eventKey="users">Users</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="admins">Admin Users</Nav.Link>
        </Nav.Item>
      </Nav>

      <Card className="users-table-card mt-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <Card.Title>
              {key === 'users' ? 'Regular Users' : 'Admin Users'}
            </Card.Title>
            <div className="d-flex align-items-center">
              <div className="summary-badge me-3">
                Total: {key === 'users' ? users.length : adminUsers.length} users
              </div>
              {key === 'users' && (
                <button 
                  className="btn btn-outline-secondary btn-sm" 
                  onClick={fetchUsers} 
                  disabled={loading}
                >
                  <FaSync className={loading ? 'spin' : ''} />
                </button>
              )}
            </div>
          </div>
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading...</p>
            </div>
          ) : (
            <Table responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  {key === 'users' || key === 'contacts' ? (
                    <>
                      <th>Join Date</th>
                      <th>Status</th>
                    </>
                  ) : (
                    <>
                      <th>Role</th>
                      <th>Last Login</th>
                    </>
                  )}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(key === 'users' ? users : adminUsers).map((user) => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>{user.phone || '-'}</td>
                    {key === 'users' ? (
                      <>
                        <td>
                          {new Date(user.joinDate).toLocaleString('en-US', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: false
                          })}
                        </td>
                        <td>
                          <span className="badge bg-success">
                            Active
                          </span>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{user.role}</td>
                        <td>{user.lastLogin}</td>
                      </>
                    )}
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="btn btn-info btn-sm" 
                          onClick={() => handleViewUser(user)}
                          title="View User Details"
                        >
                          <FaEye />
                        </button>
                        <button 
                          className="btn btn-primary btn-sm"
                          title="Edit User"
                        >
                          <FaEdit />
                        </button>
                        <button 
                          className="btn btn-danger btn-sm" 
                          onClick={() => handleDeleteUser(user.id)}
                          disabled={deleteLoading}
                          title="Delete User"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {(key === 'users' && users.length === 0 && !loading) && (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No users found. Add users to see them here.
                    </td>
                  </tr>
                )}

              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* View User Modal */}
      <Modal show={showViewModal} onHide={handleCloseViewModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>User Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <div className="message-details">
              <div className="detail-row">
                <strong>Name:</strong>
                <span>{selectedUser.name}</span>
              </div>
              <div className="detail-row">
                <strong>Email:</strong>
                <span>{selectedUser.email}</span>
              </div>
              <div className="detail-row">
                <strong>Phone:</strong>
                <span>{selectedUser.phone || 'Not provided'}</span>
              </div>
              <div className="detail-row">
                <strong>Join Date:</strong>
                <span>
                  {new Date(selectedUser.joinDate).toLocaleString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
                  })}
                </span>
              </div>
              <div className="detail-row">
                <strong>Status:</strong>
                <span>
                  <span className="badge bg-success">
                    Active
                  </span>
                </span>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseViewModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UsersManagement;
